import { all, call, put, select, takeLatest } from "typed-redux-saga";
import { alertControls, loaderControls } from "react-better-html";

import { defaultPageSize } from "../../constants/app";

import { type RootState } from "../store";
import { propertiesSlice } from "../slices/properties";

import { api } from "../../utils/api";
import { handleError } from "../../utils/functions";
import { LocalStorage } from "../../utils/localStorage";
import { i18n } from "../../utils/i18n";

function* onGetAllProperties(action: ReturnType<typeof propertiesSlice.actions.getAllProperties>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("properties:getAllProperties"));

   try {
      const response = yield* call(() =>
         api("getAllProperties", {
            path: [selectedOrganizationId, "properties"],
            query: {
               pageSize: defaultPageSize,
               ...action.payload?.query,
            },
         }),
      );

      yield* put(propertiesSlice.actions.setProperties(response.data));

      action.payload?.onSuccess?.(response.data);
   } catch (error) {
      action.payload?.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:getAllProperties"));
   }
}

function* onGetPropertyById(action: ReturnType<typeof propertiesSlice.actions.getPropertyById>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("properties:getPropertyById"));
   yield* call(() => loaderControls.startLoading(`properties:getPropertyById${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("getPropertyById", {
            path: [selectedOrganizationId, "properties", action.payload.id],
         }),
      );

      yield* put(propertiesSlice.actions.updateProperty(response.data));

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:getPropertyById"));
      yield* call(() => loaderControls.stopLoading(`properties:getPropertyById${action.payload.id}`));
   }
}

function* onSetProperties(action: ReturnType<typeof propertiesSlice.actions.setProperties>) {
   yield* call(() =>
      action.payload ? LocalStorage.setItem("properties", action.payload.items) : LocalStorage.removeItem("properties"),
   );
}

function* onCreateProperty(action: ReturnType<typeof propertiesSlice.actions.createProperty>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("properties:createProperty"));

   try {
      const response = yield* call(() =>
         api("createProperty", {
            body: action.payload.data,
            path: [selectedOrganizationId, "properties"],
         }),
      );

      const { listingScrapeData, ...rest } = response.data;

      yield* put(propertiesSlice.actions.updateProperty(rest));

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:createProperty"));
   }
}

function* onEditProperty(action: ReturnType<typeof propertiesSlice.actions.editProperty>) {
   yield* call(() => loaderControls.startLoading("properties:editProperty"));
   yield* call(() => loaderControls.startLoading(`properties:editProperty${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("editProperty", {
            body: action.payload.data,
            path: [action.payload.id],
         }),
      );

      yield* put(propertiesSlice.actions.updateProperty(response.data));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.propertyEdited),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:editProperty"));
      yield* call(() => loaderControls.stopLoading(`properties:editProperty${action.payload.id}`));
   }
}

function* onUpdateProperty() {
   const propertiesList = yield* select((state: RootState) => state.properties.list);

   yield* call(() =>
      propertiesList ? LocalStorage.setItem("properties", propertiesList.items) : LocalStorage.removeItem("properties"),
   );
}

function* onPropertyOperations(action: ReturnType<typeof propertiesSlice.actions.propertyOperations>) {
   const selectedOrganizationId = yield* select((state: RootState) => state.organizations.selectedOrganizationId);

   if (!selectedOrganizationId) return;

   yield* call(() => loaderControls.startLoading("properties:propertyOperations"));
   yield* call(() => loaderControls.startLoading(`properties:propertyOperations${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("propertyOperations", {
            body: action.payload.data,
            path: [selectedOrganizationId, "properties", action.payload.id, "operations"],
         }),
      );

      yield* put(propertiesSlice.actions.updateProperty(response.data));

      if (action.payload.data.operation === "ARCHIVE") {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.propertyArchived),
            }),
         );
      } else if (action.payload.data.operation === "UNARCHIVE") {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.propertyUnarchived),
            }),
         );
      }

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:propertyOperations"));
      yield* call(() => loaderControls.stopLoading(`properties:propertyOperations${action.payload.id}`));
   }
}

function* onGetAllWorkspaceProperties(action: ReturnType<typeof propertiesSlice.actions.getAllWorkspaceProperties>) {
   yield* call(() => loaderControls.startLoading("properties:getAllWorkspaceProperties"));

   try {
      const response = yield* call(() =>
         api("getAllWorkspaceProperties", {
            path: [action.payload.id, "properties"],
            query: {
               pageSize: defaultPageSize,
               ...action.payload?.query,
            },
         }),
      );

      yield* put(
         propertiesSlice.actions.setWorkspaceProperties({
            workspaceId: action.payload.id,
            properties: response.data,
         }),
      );

      action.payload?.onSuccess?.(response.data);
   } catch (error) {
      action.payload?.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:getAllWorkspaceProperties"));
   }
}

function* onGetWorkspacePropertyById(action: ReturnType<typeof propertiesSlice.actions.getWorkspacePropertyById>) {
   yield* call(() => loaderControls.startLoading("properties:getWorkspacePropertyById"));
   yield* call(() => loaderControls.startLoading(`properties:getWorkspacePropertyById${action.payload.id}`));

   try {
      const response = yield* call(() =>
         api("getWorkspacePropertyById", {
            path: [action.payload.workspaceId, "properties", action.payload.id],
         }),
      );

      yield* put(
         propertiesSlice.actions.updateWorkspaceProperty({
            workspaceId: action.payload.workspaceId,
            properties: response.data,
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:getWorkspacePropertyById"));
      yield* call(() => loaderControls.stopLoading(`properties:getWorkspacePropertyById${action.payload.id}`));
   }
}

function* onAddPropertyToWorkspace(action: ReturnType<typeof propertiesSlice.actions.addPropertyToWorkspace>) {
   yield* call(() => loaderControls.startLoading("properties:addPropertyToWorkspace"));

   try {
      const response = yield* call(() =>
         api("addPropertyToWorkspace", {
            body: action.payload.data,
            path: [action.payload.id, "properties"],
         }),
      );

      const { listingScrapeData, ...rest } = response.data;

      yield* put(
         propertiesSlice.actions.updateWorkspaceProperty({
            workspaceId: action.payload.id,
            properties: rest,
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("properties:addPropertyToWorkspace"));
   }
}

export default function* propertiesSaga() {
   yield* all([
      takeLatest(propertiesSlice.actions.getAllProperties, onGetAllProperties),
      takeLatest(propertiesSlice.actions.getPropertyById, onGetPropertyById),
      takeLatest(propertiesSlice.actions.setProperties, onSetProperties),
      takeLatest(propertiesSlice.actions.createProperty, onCreateProperty),
      takeLatest(propertiesSlice.actions.editProperty, onEditProperty),
      takeLatest(propertiesSlice.actions.updateProperty, onUpdateProperty),
      takeLatest(propertiesSlice.actions.propertyOperations, onPropertyOperations),
      takeLatest(propertiesSlice.actions.getAllWorkspaceProperties, onGetAllWorkspaceProperties),
      takeLatest(propertiesSlice.actions.getWorkspacePropertyById, onGetWorkspacePropertyById),
      takeLatest(propertiesSlice.actions.addPropertyToWorkspace, onAddPropertyToWorkspace),
   ]);
}
